// Reading milestones, derived entirely from a reader's own books + reading
// logs (no extra tables). Each earned milestone points at the real book that
// earned it, so the UI can show that book's cover instead of an icon.

const finishedOrder = (b) => b.dateFinished || b.dateAdded || b.created_at || '';

export const finishedBooks = (books = []) =>
  books
    .filter(b => (b.status || '').toLowerCase() === 'read')
    .sort((a, b) => String(finishedOrder(a)).localeCompare(String(finishedOrder(b))));

// Returns the date (YYYY-MM-DD) on which the reader first hit `length`
// consecutive reading days, or null.
export const firstStreakDate = (logs = [], length) => {
  const days = [...new Set(logs.map(l => l.date).filter(Boolean))].sort();
  let run = 0;
  let prev = null;
  for (const day of days) {
    const cur = new Date(day + 'T00:00:00Z');
    run = prev && (cur - prev) === 86400000 ? run + 1 : 1;
    if (run >= length) return day;
    prev = cur;
  }
  return null;
};

const nth = (n) => (finished) => finished[n - 1] || null;

const streakBook = (length) => (finished, logs, books) => {
  const day = firstStreakDate(logs, length);
  if (!day) return null;
  const log = logs.find(l => l.date === day && l.book_id);
  const book = (log && books.find(b => b.id === log.book_id))
    || [...finished].reverse().find(b => String(finishedOrder(b)) <= day)
    || { title: `${length} days`, cover: null };
  return { ...book, earnedOn: day };
};

const genreBook = (count) => (finished) => {
  const seen = new Set();
  for (const b of finished) {
    const g = (b.genre || '').trim().toLowerCase();
    if (!g || g === 'general') continue;
    seen.add(g);
    if (seen.size === count) return b;
  }
  return null;
};

export const MILESTONES = [
  { key: 'first', label: 'First Leaf', hint: 'Finish your first book', find: nth(1) },
  { key: 'rare', label: 'Rare Leaf', hint: 'Finish a 500+ page book', find: f => f.find(b => (b.totalPages || 0) >= 500) || null, detail: b => `${b.totalPages} pages` },
  { key: 'five', label: 'Five Stars', hint: 'Give a finished book 5 stars', find: f => f.find(b => (b.rating || 0) >= 5) || null },
  { key: 'listen', label: 'First Listen', hint: 'Finish an audiobook', find: f => f.find(b => b.format === 'audio') || null },
  { key: 'streak7', label: '7-Day Streak', hint: 'Read 7 days in a row', find: streakBook(7) },
  { key: 'genres', label: 'Wanderer', hint: 'Finish books in 5 genres', find: genreBook(5), detail: () => '5 genres' },
  { key: 'n10', label: '10th Book', hint: 'Finish 10 books', find: nth(10) },
  { key: 'streak30', label: '30-Day Streak', hint: 'Read 30 days in a row', find: streakBook(30) },
  { key: 'n25', label: '25th Book', hint: 'Finish 25 books', find: nth(25) },
  { key: 'n50', label: '50th Book', hint: 'Finish 50 books', find: nth(50) },
  { key: 'n100', label: '100th Book', hint: 'Finish 100 books', find: nth(100) },
];

export const computeMilestones = (books = [], logs = []) => {
  const finished = finishedBooks(books);
  return MILESTONES.map(m => {
    const book = m.find(finished, logs, books);
    return {
      key: m.key,
      label: m.label,
      hint: m.hint,
      earned: !!book,
      book,
      detail: book ? (m.detail ? m.detail(book) : (book.earnedOn || book.dateFinished || null)) : null,
    };
  });
};
